import React, { useEffect, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { postUploadPIRFile } from '../../dataClient/apiCalls'
import { BoldFormTitle, ErrorText, Subheading } from '../styles/Text'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent, FlexColumn, MedBGContainer } from '../styles/Containers'
import { ErrorMessage, InfoMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import UploadFile from '../files/UploadFile'
import BrowserFile from '../files/BrowserFile'
import PIRFile from '../files/PIRFile'
import pirTerminology from '../../util/pirTerminology'

const schema = yup.object().shape({
  file: yup.mixed().required('An authorization file is required')
})

function PIRAuthorizationButton({ pirId, authorizerRole, currentFileName = '', onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const methods = useForm({ resolver: yupResolver(schema) })
  const { handleSubmit, watch, formState, setValue, reset } = methods

  const file = watch('file')

  const isMounted = useRef(true)
  const action = currentFileName ? 'Replace' : 'Upload'

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleOpen = () => setIsVisible(true)
  function handleClose() {
    reset()
    setError(false)
    setIsVisible(false)
    setIsLoading(false)
  }

  const handleUploadFiles = acceptedFiles => setValue('file', acceptedFiles[0])
  const handleDelete = () => setValue('file', undefined)

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const isSignature = true
    const authorizer = authorizerRole.toLowerCase()

    postUploadPIRFile(pirId, data.file, isSignature, authorizer)
      .then(() => {
        handleClose()
        onSuccess()
      })
      .catch(e => {
        console.error(`failed to upload the ${authorizerRole} signature file for PIR with ID`, pirId, file, e)
        setError(true)
        setIsLoading(false)
      })
  }

  const fileError = !!formState.errors.file
  const fileHelperText = formState.errors?.file?.message

  return (
    <>
      <SecondaryButton onClick={handleOpen}>{action} Authorization File</SecondaryButton>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <BoldFormTitle>{pirTerminology.getAuthorizerDialogTitle(action, authorizerRole)}</BoldFormTitle>

              {action === 'Replace' ? (
                <>
                  <Subheading>Existing Authorization File</Subheading>
                  {currentFileName && (
                    <MedBGContainer padding="5px 20px 5px 5px">
                      <PIRFile file={{ name: currentFileName }} pirId={pirId} buttonAction="download" type="signature" />
                    </MedBGContainer>
                  )}
                  <Subheading>New Authorization File</Subheading>
                  <InfoMessage>Uploading a new authorization file will replace the existing authorization file.</InfoMessage>
                </>
              ) : (
                <InfoMessage>Uploading an authorization file will mark this signature as complete.</InfoMessage>
              )}

              {file ? (
                <MedBGContainer padding="5px 20px 5px 5px">
                  <BrowserFile file={file} onDelete={handleDelete} loading={isLoading} />
                </MedBGContainer>
              ) : (
                <FlexColumn gap="5px">
                  <UploadFile onUpload={handleUploadFiles} maxFiles={1} />
                  {fileError && <ErrorText role="alert">{fileHelperText}</ErrorText>}
                </FlexColumn>
              )}

              {error && <ErrorMessage>Hm, an error occurred uploading the file. Try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  {action === 'Replace' ? 'Confirm & Replace File' : 'Upload File'}
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

PIRAuthorizationButton.propTypes = {
  pirId: PropTypes.string.isRequired,
  authorizerRole: PropTypes.string.isRequired,
  currentFileName: PropTypes.string,
  onSuccess: PropTypes.func
}

export default PIRAuthorizationButton
